import { Component, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ENVIRONMENT } from "@company-box/models";
import { RedesignVariantService } from "../../services/redesign-variant.service";

// Path prefixes with a real redesigned sibling. Anything else
// has no counterpart - the toggle just flips the flag in place.
const REDESIGN_PREFIXES = [
  "/start-redesign",
  "/box-category-redesign",
  "/box-style-redesign",
  "/box-options-redesign",
  "/design-3d-redesign",
  "/account-redesign",
  "/order-history-redesign",
  "/online-designs-redesign",
  "/upload-design-redesign",
  "/offline-designs-redesign",
  "/cart-redesign",
  "/review-box-redesign",
  "/samples-redesign",
  "/login-redesign",
];
const CLASSIC_PREFIXES = [
  "/start",
  "/box-category",
  "/box-style",
  "/box-options",
  "/design-3d",
  "/account",
  "/order-history",
  "/continue-project",
  "/upload-design",
  "/continue-project", // Offline designs have no classic page; the classic list is the closest.
  "/cart",
  "/review-box",
  "/samples",
  "/login",
];

/**
 * Small floating on/off switch for the redesign review flag - lets whoever's
 * reviewing flip between the classic and redesigned experience from either
 * side without editing the URL. Mounted once in CommonLayoutComponent (classic
 * pages) and once in RedesignShellComponent (redesigned pages), so it's
 * visible everywhere regardless of which experience is currently showing.
 */
@Component({
  selector: "app-redesign-toggle",
  templateUrl: "./redesign-toggle.component.html",
  styleUrls: ["./redesign-toggle.component.scss"],
})
export class RedesignToggleComponent {
  private variant = inject(RedesignVariantService);
  private router = inject(Router);
  private environment = inject(ENVIRONMENT);

  // local, local-sandbox, and sandbox all have production:false; only the
  // production build sets it true, so this alone is enough to hide the
  // toggle there without needing a dedicated per-environment flag.
  showToggle = !this.environment.production;

  enabled = signal(this.variant.isRedesign());

  toggle(): void {
    const next = !this.enabled();
    this.enabled.set(next);
    this.variant.setRedesign(next);

    if (next) {
      // Classic routes carry the redirect guard themselves - reloading re-runs
      // it, which now sends us to the matching -redesign page (or does
      // nothing, if the current page has no redesigned counterpart).
      window.location.reload();
      return;
    }

    const url = this.router.url;
    const fromIndex = REDESIGN_PREFIXES.findIndex(
      (prefix) => url === prefix || url.startsWith(prefix + "/") || url.startsWith(prefix + ";")
    );
    if (fromIndex === -1) {
      return;
    }
    const prefix = REDESIGN_PREFIXES[fromIndex];
    const target = CLASSIC_PREFIXES[fromIndex] + url.slice(prefix.length);
    this.router.navigateByUrl(target);
  }
}

